import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, Pencil, Check, ChevronDown, ChevronUp, CalendarOff } from 'lucide-react';
import Button from './Button';
import DateInput, { DateInputRef } from './DateInput';
import OptionToggle from './OptionToggle';
import {
  RecurrenceConfig,
  RecurrenceFrequency,
} from '../../types';

interface RecurrenceConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: RecurrenceConfig) => void;
  config: RecurrenceConfig | null;
  operationDate: string;
  defaultMonth?: number;
  defaultYear?: number;
}

/**
 * Ligne de pause interne à la modale : porte un id stable (clé React + handlers
 * robustes aux réordonnancements), un drapeau `editing` qui bascule l'affichage
 * entre résumé texte (compact) et zones de dates (édition), et `isNew` qui
 * distingue une pause ajoutée dans la session courante (affichée au-dessus, en
 * cours de configuration) d'une pause déjà enregistrée (rangée dans l'historique).
 */
interface PauseRow {
  id: string;
  start: string;
  end: string;
  editing: boolean;
  isNew: boolean;
}

const makeRowId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const isPauseRowComplete = (row: { start: string; end: string }) =>
  !!row.start && !!row.end && row.end >= row.start;

/**
 * Calcule le jour de la semaine d'une date (0=Lundi, 6=Dimanche)
 */
function getWeekdayFromDate(dateStr: string): number {
  const date = new Date(dateStr);
  const jsDay = date.getUTCDay(); // 0=Dimanche, 1=Lundi, ..., 6=Samedi
  return jsDay === 0 ? 6 : jsDay - 1;
}

/**
 * Calcule l'ordinal du jour dans le mois (1er, 2e, 3e, 4e, dernier)
 */
function getWeekdayOrdinalFromDate(dateStr: string): number {
  const date = new Date(dateStr);
  const dayOfMonth = date.getUTCDate();
  const ordinal = Math.ceil(dayOfMonth / 7);

  // Vérifier si c'est le dernier de son genre dans le mois
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const nextWeek = new Date(Date.UTC(year, month, dayOfMonth + 7, 12, 0, 0, 0));
  if (nextWeek.getUTCMonth() !== month) {
    return 5; // C'est le dernier
  }

  return ordinal;
}

export default function RecurrenceConfigModal({
  isOpen,
  onClose,
  onSave,
  config,
  operationDate,
  defaultMonth,
  defaultYear,
}: RecurrenceConfigModalProps) {
  const { t, i18n } = useTranslation('common');

  // Formate un jour « YYYY-MM-DD » selon la locale active (parse en UTC pour
  // éviter tout décalage de fuseau qui ferait glisser d'un jour).
  const formatDay = (s: string): string => {
    if (!s) return '';
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return s;
    return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const WEEKDAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const WEEKDAY_LABELS = WEEKDAY_KEYS.map(key => t(`weekdays.${key}`));
  const ORDINAL_LABELS: Record<number, string> = {
    1: t('ordinals.1'),
    2: t('ordinals.2'),
    3: t('ordinals.3'),
    4: t('ordinals.4'),
    5: t('ordinals.5'),
  };

  const [frequency, setFrequency] = useState<RecurrenceFrequency>('MONTHLY_DAY');
  const [weekday, setWeekday] = useState<number>(0);
  const [weekdayOrdinal, setWeekdayOrdinal] = useState<number>(1);
  const [interval, setInterval] = useState<number>(2);
  const [monthInterval, setMonthInterval] = useState<number>(2);
  const [totalCount, setTotalCount] = useState<number>(4);
  const [hasEndDate, setHasEndDate] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<string>('');
  const endDateRef = useRef<DateInputRef | null>(null);
  const shouldFocusEndDateRef = useRef<boolean>(false);
  // Liste des fenêtres de pause. Une occurrence unique ignorée = fenêtre start === end.
  const [pauses, setPauses] = useState<PauseRow[]>([]);
  // Déroulé de toute la section pause (repliée par défaut : l'en-tête « Pauses » suffit).
  const [showPauseSection, setShowPauseSection] = useState<boolean>(false);
  // Déroulé de l'historique des pauses enregistrées (repliée par défaut).
  const [showPauseList, setShowPauseList] = useState<boolean>(false);

  useEffect(() => {
    if (hasEndDate && shouldFocusEndDateRef.current) {
      shouldFocusEndDateRef.current = false;
      requestAnimationFrame(() => endDateRef.current?.focus());
    }
  }, [hasEndDate]);

  // Initialiser les valeurs depuis la config ou la date de l'opération
  useEffect(() => {
    if (config) {
      setFrequency(config.frequency);
      setWeekday(config.weekday ?? getWeekdayFromDate(operationDate));
      setWeekdayOrdinal(config.weekdayOrdinal ?? getWeekdayOrdinalFromDate(operationDate));
      setInterval(config.interval ?? 2);
      setMonthInterval(config.frequency === 'EVERY_N_MONTHS' ? (config.interval ?? 2) : 2);
      setTotalCount(config.totalCount ?? 4);
      setHasEndDate(!!config.endDate);
      setEndDate(config.endDate ? config.endDate.substring(0, 10) : '');
      setPauses(
        config.pauses
          ? config.pauses.map((p) => ({
              id: makeRowId(),
              start: p.start ? p.start.substring(0, 10) : '',
              end: p.end ? p.end.substring(0, 10) : '',
              editing: false,
              isNew: false,
            }))
          : []
      );
      setShowPauseList(false);
      setShowPauseSection(false);
    } else {
      // Valeurs par défaut basées sur la date de l'opération
      setFrequency('MONTHLY_DAY');
      setWeekday(getWeekdayFromDate(operationDate));
      setWeekdayOrdinal(getWeekdayOrdinalFromDate(operationDate));
      setInterval(2);
      setMonthInterval(2);
      setTotalCount(4);
      setHasEndDate(false);
      setEndDate('');
      setPauses([]);
      setShowPauseList(false);
      setShowPauseSection(false);
    }
  }, [config, operationDate, isOpen]);

  // Handlers de gestion de la liste de pauses (indexés par id stable).
  // Ajouter une pause « période » : nouvelle ligne vide, ouverte en édition, en
  // cours de configuration (isNew) → affichée au-dessus de l'historique.
  const addPause = () => {
    setPauses((prev) => [...prev, { id: makeRowId(), start: '', end: '', editing: true, isNew: true }]);
  };
  const removePause = (id: string) => setPauses((prev) => prev.filter((p) => p.id !== id));
  const updatePause = (id: string, field: 'start' | 'end', value: string) =>
    setPauses((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  const setPauseEditing = (id: string, editing: boolean) =>
    setPauses((prev) => prev.map((p) => (p.id === id ? { ...p, editing } : p)));
  // Raccourci : mettre en pause uniquement l'occurrence ouverte (fenêtre d'un jour,
  // universel pour toutes les fréquences — hebdo comprise). Ajoutée déjà complète,
  // donc affichée directement en résumé texte, en cours de configuration (isNew).
  const pauseThisOccurrence = () => {
    const day = operationDate.substring(0, 10);
    setPauses((prev) => [...prev, { id: makeRowId(), start: day, end: day, editing: false, isNew: true }]);
  };

  const handleSave = () => {
    const newConfig: RecurrenceConfig = {
      frequency,
      day: new Date(operationDate).getUTCDate(),
    };

    // Ajouter les champs selon le type de récurrence
    if (frequency === 'MONTHLY_WEEKDAY') {
      newConfig.weekday = weekday;
      newConfig.weekdayOrdinal = weekdayOrdinal;
    } else if (frequency === 'WEEKLY') {
      newConfig.weekday = weekday;
      newConfig.interval = 1;
    } else if (frequency === 'EVERY_N_WEEKS') {
      newConfig.weekday = weekday;
      newConfig.interval = interval;
    } else if (frequency === 'EVERY_N_MONTHS') {
      newConfig.interval = monthInterval;
    } else if (frequency === 'YEARLY') {
      // Pas de champs supplémentaires, la date de l'opération définit le jour et le mois
    } else if (frequency === 'INSTALLMENTS') {
      newConfig.totalCount = totalCount;
    }

    // Ajouter ou effacer la date de fin
    if (hasEndDate && endDate && frequency !== 'INSTALLMENTS') {
      newConfig.endDate = endDate;
    } else if (!hasEndDate) {
      newConfig.endDate = null; // Signal explicite pour effacer la date de fin
    }

    // Pauses de prélèvement (s'applique à toutes les fréquences). On ne conserve que
    // les fenêtres complètes et cohérentes. Un tableau vide efface les pauses.
    newConfig.pauses = pauses
      .filter(isPauseRowComplete)
      .map((p) => ({ start: p.start, end: p.end }));

    onSave(newConfig);
    onClose();
  };

  if (!isOpen) return null;

  const dayOfMonth = new Date(operationDate).getUTCDate();
  const currentWeekday = getWeekdayFromDate(operationDate);
  const currentOrdinal = getWeekdayOrdinalFromDate(operationDate);

  // Pauses en cours de configuration (session courante) vs déjà enregistrées.
  const stagedPauses = pauses.filter((p) => p.isNew);
  const historyPauses = pauses.filter((p) => !p.isNew);
  // Nombre de pauses réelles (complètes) pour le badge de l'en-tête replié.
  const completePauseCount = pauses.filter(isPauseRowComplete).length;

  // Rendu d'une ligne de pause : zones de dates si en édition, sinon résumé texte.
  const renderPauseRow = (p: PauseRow) => {
    if (p.editing) {
      // Le champ « fin » s'ouvre sur le mois du « début » (évite un calendrier
      // ouvert sur un mois antérieur entièrement grisé).
      const endDefaultMonth = p.start ? Number(p.start.substring(5, 7)) : defaultMonth;
      const endDefaultYear = p.start ? Number(p.start.substring(0, 4)) : defaultYear;
      const complete = isPauseRowComplete(p);
      return (
        <div key={p.id} className="flex items-end gap-2 p-2 bg-gray-50 rounded-lg">
          <div className="flex-1 min-w-0">
            <label className="block text-xs text-gray-500 mb-1">
              {t('recurrence.pauseStartLabel')}
            </label>
            <DateInput
              value={p.start}
              onChange={(date) => updatePause(p.id, 'start', date)}
              minDate={operationDate}
              defaultMonth={defaultMonth}
              defaultYear={defaultYear}
            />
          </div>
          <div className="flex-1 min-w-0">
            <label className="block text-xs text-gray-500 mb-1">
              {t('recurrence.pauseEndLabel')}
            </label>
            <DateInput
              value={p.end}
              onChange={(date) => updatePause(p.id, 'end', date)}
              minDate={p.start || operationDate}
              defaultMonth={endDefaultMonth}
              defaultYear={endDefaultYear}
            />
          </div>
          {complete && (
            <button
              type="button"
              onClick={() => setPauseEditing(p.id, false)}
              aria-label={t('recurrence.confirmPause')}
              className="p-1.5 mb-1 text-gray-400 hover:text-green-600 transition-colors"
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => removePause(p.id)}
            aria-label={t('recurrence.removePause')}
            className="p-1.5 mb-1 text-gray-400 hover:text-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      );
    }
    // Mode résumé texte : occurrence unique ignorée vs période.
    const label = p.start === p.end
      ? t('recurrence.occurrenceSkipped', { date: formatDay(p.start) })
      : t('recurrence.pausedFrom', { start: formatDay(p.start), end: formatDay(p.end) });
    return (
      <div key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-lg">
        <span className="text-sm text-gray-700">{label}</span>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setPauseEditing(p.id, true)}
            aria-label={t('recurrence.editPause')}
            className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => removePause(p.id)}
            aria-label={t('recurrence.removePause')}
            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('recurrence.configTitle')}
          </h3>

          {/* Fréquence */}
          <div className="space-y-3 mb-6">
            <label className="block text-sm font-medium text-gray-700">
              {t('recurrence.frequency')}
            </label>

            <div className="space-y-2">
              {([
                ['MONTHLY_DAY', t('recurrence.monthlyDay', { day: dayOfMonth })],
                ['MONTHLY_WEEKDAY', t('recurrence.monthlyWeekday', { ordinalLabel: ORDINAL_LABELS[currentOrdinal], weekday: WEEKDAY_LABELS[currentWeekday]?.toLowerCase() })],
                ['WEEKLY', t('recurrence.weeklyEvery', { weekday: WEEKDAY_LABELS[currentWeekday]?.toLowerCase() })],
                ['EVERY_N_WEEKS', t('recurrence.everyNWeeks')],
                ['EVERY_N_MONTHS', t('recurrence.everyNMonths')],
                ['YEARLY', t('recurrence.yearlyOn', { day: dayOfMonth, month: (() => {
                  const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
                  return t(`months.${MONTH_KEYS[new Date(operationDate).getUTCMonth()]}`);
                })() })],
                ['INSTALLMENTS', t('recurrence.installmentsPayments')],
              ] as [RecurrenceFrequency, string][]).map(([value, label]) => (
                <OptionToggle
                  key={value}
                  variant="row"
                  selected={frequency === value}
                  onToggle={() => setFrequency(value)}
                >
                  {label}
                </OptionToggle>
              ))}
            </div>
          </div>

          {/* Options selon la fréquence */}
          {frequency === 'MONTHLY_WEEKDAY' && (
            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('recurrence.dayOfMonth')}
              </label>
              <div className="flex gap-2">
                <select
                  value={weekdayOrdinal}
                  onChange={(e) => setWeekdayOrdinal(Number(e.target.value))}
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                >
                  {Object.entries(ORDINAL_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <select
                  value={weekday}
                  onChange={(e) => setWeekday(Number(e.target.value))}
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                >
                  {WEEKDAY_LABELS.map((label, index) => (
                    <option key={index} value={index}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {frequency === 'EVERY_N_WEEKS' && (
            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('recurrence.interval')}
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t('recurrence.everyPrefix')}</span>
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={interval}
                  onChange={(e) => setInterval(Math.max(1, Math.min(99, Number(e.target.value))))}
                  className="w-16 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm text-center"
                />
                <span className="text-sm text-gray-600">{t('recurrence.weeksSuffix')}</span>
              </div>
            </div>
          )}

          {frequency === 'EVERY_N_MONTHS' && (
            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('recurrence.interval')}
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t('recurrence.everyPrefixMasc')}</span>
                <input
                  type="number"
                  min="2"
                  max="99"
                  value={monthInterval}
                  onChange={(e) => setMonthInterval(Math.max(2, Math.min(99, Number(e.target.value))))}
                  className="w-16 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm text-center"
                />
                <span className="text-sm text-gray-600">{t('recurrence.monthsSuffix')}</span>
              </div>
            </div>
          )}

          {frequency === 'INSTALLMENTS' && (
            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('recurrence.paymentCount')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="2"
                  max="99"
                  value={totalCount}
                  onChange={(e) => setTotalCount(Math.max(2, Math.min(99, Number(e.target.value))))}
                  className="w-16 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm text-center"
                />
                <span className="text-sm text-gray-600">{t('recurrence.monthlyPayments')}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('recurrence.allCreatedImmediately')}
              </p>
            </div>
          )}

          {/* Date de fin (sauf pour INSTALLMENTS) */}
          {frequency !== 'INSTALLMENTS' && (
            <div className="mb-6">
              <OptionToggle
                selected={hasEndDate}
                onToggle={() => {
                  if (!hasEndDate) shouldFocusEndDateRef.current = true;
                  setHasEndDate(!hasEndDate);
                }}
                className="mb-2"
              >
                {t('recurrence.setEndDate')}
              </OptionToggle>
              {hasEndDate && (
                <DateInput
                  ref={endDateRef}
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  minDate={operationDate}
                  defaultMonth={defaultMonth}
                  defaultYear={defaultYear}
                />
              )}
            </div>
          )}

          {/* Pauses de prélèvement (disponible pour toutes les fréquences) — section repliable */}
          <div className="mb-6">
            {/* En-tête cliquable : replié par défaut, badge du nombre de pauses configurées. */}
            <button
              type="button"
              onClick={() => setShowPauseSection((v) => !v)}
              className="w-full flex items-center justify-between text-sm font-medium text-gray-700"
            >
              <span className="inline-flex items-center gap-2">
                {t('recurrence.pauseLabel')}
                {completePauseCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold text-primary-700 bg-primary-100 rounded-full">
                    {completePauseCount}
                  </span>
                )}
              </span>
              {showPauseSection
                ? <ChevronUp className="w-4 h-4 text-gray-500" />
                : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </button>

            {showPauseSection && (
            <div className="mt-3">
            {/* 1. Raccourci distinctif : sauter l'occurrence courante. Encadré teinté,
                   bordure pointillée, placé en tête, indépendant des liens. */}
            <button
              type="button"
              onClick={pauseThisOccurrence}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 mb-2 text-sm font-medium text-primary-700 bg-primary-50 border border-dashed border-primary-300 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <CalendarOff className="w-4 h-4" />
              {t('recurrence.pauseThisOccurrence')}
            </button>

            {/* 2. Ajouter une pause « période » : ouvre une nouvelle ligne en édition. */}
            <button
              type="button"
              onClick={addPause}
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 mb-3"
            >
              <Plus className="w-4 h-4" />
              {t('recurrence.addPause')}
            </button>

            {/* 3. Pauses en cours de configuration (session courante), au-dessus de l'historique. */}
            {stagedPauses.length > 0 && (
              <div className="space-y-2 mb-3">
                {stagedPauses.map(renderPauseRow)}
              </div>
            )}

            {/* 4. Historique repliable des pauses déjà enregistrées. */}
            {historyPauses.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setShowPauseList((v) => !v)}
                  className="w-full flex items-center justify-between text-sm text-gray-600 hover:text-gray-800 mb-2"
                >
                  <span>{t('recurrence.pausesConfigured', { count: historyPauses.length })}</span>
                  <span className="inline-flex items-center gap-1 text-primary-600">
                    {showPauseList ? t('recurrence.hidePauses') : t('recurrence.showAllPauses')}
                    {showPauseList ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </span>
                </button>

                {showPauseList && (
                  <div className="space-y-2 mb-1">
                    {historyPauses.map(renderPauseRow)}
                  </div>
                )}
              </>
            )}

            {pauses.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                {t('recurrence.pauseHint')}
              </p>
            )}
            </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {t('save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
